import { styled } from '@mui/material'
import { useCallback, useState } from 'react'
import { useVersionReportVersionRepository } from '../../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import { dateTimeV2Service } from '../../../../../domain/value-object/DateTimeV2'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import FolderRoundedIcon from '@mui/icons-material/FolderRounded'
import { UseCurrentVersionReturnValue } from '../../hooks/currentReport'
import AlertDialog from '../../../../components/dialogs/AlertDialog'
import { VersionInfo } from './VersionInfo'
import { VersionReportMenu } from './ReportMenu'
import { DiscussionButton } from './DiscussionButton'
import { ReportMenuButton } from './ReportMenuButton'
import { PageConfigButton } from './PageConfigButton'
import { RefreshButton } from './RefreshButton'
import { UseDiscussionPanelReturnValue } from '../../hooks/useDiscussionPanel'
import { useDialogState } from '../../../../components/dialogs/useDialogState'
import { useUrlQuery } from '../../hooks/useUrlQuery'
import { PAGE_TYPE_LABELS } from '../dialog/VersionConfigDialog/constants'
import { HeaderIconButton } from './common'

type VersionReportHeaderProps = {
  onEvaluatePage?: () => void
  discussionPanel: UseDiscussionPanelReturnValue
  isVersionSelectMenuOpen: boolean
  onReportMenuToggle: () => void
  onAfterDeleteVersion: () => Promise<void>
  isVersionConfigOpen: boolean
  onToggleVersionConfig: () => void
  showLoading: () => void
  hideLoading: () => void
} & Omit<
  UseCurrentVersionReturnValue,
  | 'updatePageTitle'
  | 'updateLeadSentence'
  | 'commitPageContent'
  | 'displayState'
  | 'updateDisplayState'
  | 'updateVersionUuid'
  | 'updateCurrentVersion'
  | 'storedVersionUuid'
  | 'initialized'
  | 'save'
  | 'isDirty'
  | 'changeLength'
  | 'isSaving'
  | 'discardDraft'
>
export const VersionReportHeader = ({
  currentVersion,
  currentPage,
  currentPageIndex,
  toPrevPage,
  toNextPage,
  onEvaluatePage,
  discussionPanel,
  isVersionSelectMenuOpen,
  onReportMenuToggle,
  onAfterDeleteVersion,
  isVersionConfigOpen,
  onToggleVersionConfig,
  reCalculateAndFetchVersion,
  refetchVersion,
  showLoading,
  hideLoading,
}: VersionReportHeaderProps) => {
  const { deleteVersion } = useVersionReportVersionRepository()
  const { toUrlWithVersionUuid } = useUrlQuery()
  const deleteCurrentVersion = useCallback(async () => {
    if (!currentVersion) return
    await deleteVersion(currentVersion.uuid)
  }, [currentVersion, deleteVersion])

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
  const openDeleteDialog = useCallback(() => setDeleteDialogOpen(true), [])
  const closeDeleteDialog = useCallback(() => setDeleteDialogOpen(false), [])
  const [recalculateDialogOpen, openRecalculateDialog, closeRecalculateDialog] =
    useDialogState()
  const onDelete = useCallback(async () => {
    await deleteCurrentVersion()
    closeDeleteDialog()
    await onAfterDeleteVersion()
  }, [closeDeleteDialog, deleteCurrentVersion, onAfterDeleteVersion])
  const onReCalculate = useCallback(async () => {
    closeRecalculateDialog()
    showLoading()
    await reCalculateAndFetchVersion()
    hideLoading()
  }, [
    closeRecalculateDialog,
    showLoading,
    reCalculateAndFetchVersion,
    hideLoading,
  ])
  const onCopyUrl = useCallback(() => {
    if (!currentVersion) return
    const url = toUrlWithVersionUuid(currentVersion.uuid)
    navigator.clipboard.writeText(url)
  }, [toUrlWithVersionUuid, currentVersion])
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  const onRefresh = useCallback(async () => {
    if (isRefreshing) return
    setIsRefreshing(true)
    showLoading()
    try {
      await refetchVersion()
    } finally {
      hideLoading()
      setIsRefreshing(false)
    }
  }, [isRefreshing, showLoading, refetchVersion, hideLoading])
  return (
    <>
      <HeaderRoot>
        <LeftSideArea>
          <ReportMenuButton
            isOpen={isVersionSelectMenuOpen}
            onToggle={onReportMenuToggle}
          />
          {currentVersion && (
            <PageConfigButton
              isOpen={isVersionConfigOpen}
              onToggle={onToggleVersionConfig}
            />
          )}
          {currentVersion && (
            <RefreshButton onClick={onRefresh} disabled={isVersionConfigOpen} />
          )}
          {currentVersion && (
            <VersionTitleArea>
              <TemplateNameRow>
                <FolderIcon />
                <TemplateName>{currentVersion.templateName}</TemplateName>
              </TemplateNameRow>
              <ReportName>{currentVersion.name}</ReportName>
            </VersionTitleArea>
          )}
          <PageNav>
            <PagingComponent
              current={currentPageIndex + 1}
              total={currentVersion?.pages.length || 0}
              toPrev={toPrevPage}
              toNext={toNextPage}
            />
            {currentPage && (
              <PageLabel>
                <TypeDot />
                <PageLabelText>
                  {PAGE_TYPE_LABELS[currentPage.pageType]}
                </PageLabelText>
              </PageLabel>
            )}
          </PageNav>
        </LeftSideArea>
        {currentVersion && (
          <RightSideArea>
            <UpdatedInfo>
              <UpdatedLabel>最終更新日時：</UpdatedLabel>
              <UpdatedValue>
                {dateTimeV2Service.format(currentVersion.updatedAt)}
              </UpdatedValue>
              <UpdatedLabel>最終更新者：</UpdatedLabel>
              <UpdatedValue>{currentVersion.updatedBy.name}</UpdatedValue>
            </UpdatedInfo>
            <DiscussionButton
              isOpen={discussionPanel.isOpen}
              disabled={isVersionConfigOpen}
              onToggle={discussionPanel.toggle}
            />
            <VersionInfo version={currentVersion} />
            <VersionReportMenu
              onDelete={openDeleteDialog}
              onReCalculate={openRecalculateDialog}
              onCopyUrl={onCopyUrl}
              onEvaluatePage={!isVersionConfigOpen ? onEvaluatePage : undefined}
              isEvaluatePageDisabled={
                currentPage?.pageType === 'PROGRESS_OVERVIEW'
              }
            />
          </RightSideArea>
        )}
      </HeaderRoot>
      <AlertDialog
        isOpen={deleteDialogOpen}
        title={intl.formatMessage({ id: 'versionReport.delete.title' })}
        message={intl.formatMessage({ id: 'versionReport.delete.message' })}
        submitHandler={onDelete}
        closeHandler={closeDeleteDialog}
      />
      <AlertDialog
        isOpen={recalculateDialogOpen}
        title={intl.formatMessage({ id: 'versionReport.recalculate.title' })}
        message={intl.formatMessage({
          id: 'versionReport.recalculate.message',
        })}
        submitHandler={onReCalculate}
        closeHandler={closeRecalculateDialog}
      />
    </>
  )
}

const HeaderRoot = styled('div')({
  display: 'flex',
  backgroundColor: colorPalette.monotone[0],
  borderRadius: '4px',
  padding: '12px',
  alignItems: 'center',
  justifyContent: 'space-between',
})
const LeftSideArea = styled('div')({
  display: 'flex',
  gap: 'clamp(6px, 1vw, 12px)',
  alignItems: 'center',
})
const RightSideArea = styled('div')({
  display: 'flex',
  gap: 'clamp(6px, 1vw, 12px)',
  alignItems: 'center',
})
type PagingComponentProps = {
  current: number
  total: number
  toPrev: () => void
  toNext: () => void
}
const PagingComponent = ({
  current,
  total,
  toPrev,
  toNext,
}: PagingComponentProps) => {
  const isEmpty = total === 0
  return (
    <PagingComponentRoot>
      <PagingButton onClick={toPrev} disabled={isEmpty}>
        <PrevIcon />
      </PagingButton>
      <CountSegment>
        <CurrentText>{isEmpty ? 0 : current}</CurrentText>
        <TotalText>/ {total}</TotalText>
      </CountSegment>
      <PagingButton onClick={toNext} disabled={isEmpty}>
        <NextIcon />
      </PagingButton>
    </PagingComponentRoot>
  )
}

const PagingComponentRoot = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
})

const PagingButton = styled(HeaderIconButton)({
  '&:disabled': {
    cursor: 'default',
  },
})

const PrevIcon = styled(NavigateBeforeRoundedIcon)({
  color: colorPalette.monotone[4],
  fontSize: '16px',
})
const NextIcon = styled(NavigateNextRoundedIcon)({
  color: colorPalette.monotone[4],
  fontSize: '16px',
})

const CountSegment = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  whiteSpace: 'nowrap',
})

const TypeDot = styled('span')({
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: colorPalette.monotone[5],
  flexShrink: 0,
})

const CurrentText = styled('span')({
  color: colorPalette.monotone[10],
  fontSize: '13px',
  fontWeight: 700,
})
const TotalText = styled('span')({
  color: colorPalette.monotone[7],
  fontSize: '12px',
})

const VersionTitleArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '2px',
  minWidth: 0,
})

const TemplateNameRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: colorPalette.monotone[6],
})

const FolderIcon = styled(FolderRoundedIcon)({
  fontSize: '14px',
  color: colorPalette.monotone[5],
})

const TemplateName = styled('span')({
  fontSize: '12px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
})

const ReportName = styled('span')({
  color: colorPalette.monotone[10],
  fontSize: 'clamp(15px, 1.2vw, 18px)',
  fontWeight: 700,
  whiteSpace: 'nowrap',
})

const PageNav = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'clamp(6px, 1vw, 12px)',
})

const PageLabel = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
})

const PageLabelText = styled('span')({
  color: colorPalette.monotone[10],
  fontSize: 'clamp(12px, 1vw, 14px)',
  fontWeight: 500,
  whiteSpace: 'nowrap',
})

const UpdatedInfo = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'max-content max-content',
  columnGap: '2px',
  color: colorPalette.monotone[5],
  fontSize: '12px',
  lineHeight: 1.3,
})
const UpdatedLabel = styled('span')({
  textAlign: 'right',
})
const UpdatedValue = styled('span')({
  textAlign: 'left',
})
